import React, { forwardRef } from 'react';
import type { CSSObject } from 'styled-components';
import styled from 'styled-components';
import type {
  BackgroundColorProps,
  BackgroundProps,
  BorderProps,
  FlexboxProps,
  LayoutProps,
  PositionProps,
  SpaceProps,
  OpacityProps,
} from 'styled-system';
import { border, color, flexbox, layout, position, space } from 'styled-system';

/**
 * Copied from react-native-web View styles for compatibility
 * https://github.com/necolas/react-native-web/blob/67a0bde7b3c15fd2b47a42978d7f9f3ffcda3ff6/packages/react-native-web/src/exports/View/index.js#L146
 */
const viewStyles: CSSObject = {
  alignItems: 'stretch',
  backgroundColor: 'transparent',
  border: '0 solid black',
  boxSizing: 'border-box',
  display: 'flex',
  flexBasis: 'auto',
  flexDirection: 'column',
  flexShrink: 0,
  listStyle: 'none',
  margin: 0,
  minHeight: 0,
  minWidth: 0,
  padding: 0,
  position: 'relative',
  textDecoration: 'none',
  zIndex: 0,
};

export interface ViewProps
  extends SpaceProps,
    LayoutProps,
    FlexboxProps,
    BackgroundProps,
    BackgroundColorProps,
    BorderProps,
    PositionProps,
    OpacityProps {}

type BackingProps = React.JSX.IntrinsicAttributes & {
  'data-testid'?: string;
} & React.RefAttributes<HTMLDivElement> &
  Pick<
    React.HTMLAttributes<HTMLDivElement>,
    | 'className'
    | 'children'
    | 'onClick'
    | 'style'
    | 'role'
    | 'aria-label'
    | 'aria-selected'
  > &
  ViewProps;

const Div = forwardRef(
  (props: BackingProps, ref: React.Ref<HTMLDivElement>) => {
    const { className, children, onClick, style, role } = props;
    return (
      <div
        data-testid={props['data-testid']}
        aria-selected={props['aria-selected']}
        aria-label={props['aria-label']}
        className={className}
        onClick={onClick}
        style={style}
        role={role}
        ref={ref}
      >
        {children}
      </div>
    );
  },
);

const View = styled(Div)<BackingProps>(
  viewStyles,
  border,
  color,
  flexbox,
  layout,
  position,
  space,
);

export default View;
